/**
 * @module ContainerTriagePanel
 * @purpose Modal panel for grouped/filterable artefact triage (AC7): loads a
 *   receipt's container inventory, lets the analyst select items (with a
 *   gated re-ingest confirmation for already-ingested items), and submits a
 *   selective ingestion run, showing the shared OperationProgress facility
 *   inline both for the inventory-trigger sub-operation and, once a run
 *   starts, for the run itself (AC14) -- the creation response's
 *   `operationId` is best-effort, so a `null` value falls back to the plain
 *   submitted summary rather than erroring.
 * @depends use-container-triage, container-triage.parts, components/common
 *   (OperationProgress), components/ConfirmDialog
 * @dependedOnBy pages/case/CaseProcessingTab (mounted per selected receipt)
 *
 * @rules
 * - State: delegated entirely to useContainerTriage; this file is composition
 * - Errors: all six phases (loading/ready/confirmingReingest/submitting/
 *   submitted/error) render distinct, honest UI -- no silent no-ops
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '../../ConfirmDialog'
import { OperationProgress } from '../../common'
import {
  EmptyGroupsState,
  GroupHeader,
  ItemRow,
  KindFilterSelect,
  LoadErrorState,
  NeedsInventoryState,
  PanelHeader,
  PanelLoadingState,
  SelectionFooter,
  SubmitErrorBanner,
  SubmittedState,
} from './container-triage.parts'
import { useContainerTriage } from './use-container-triage'
import type { ContainerTriageState } from './container-triage.types'

export interface ContainerTriagePanelProps {
  caseId: string
  receiptId: string
  onClose: () => void
}

function GroupsList({
  state,
}: {
  state: ContainerTriageState
}): ReactElement {
  const { t } = useTranslation('cases')
  if (state.visibleGroups.length === 0) return <EmptyGroupsState t={t} />
  return (
    <div className="space-y-1">
      {state.visibleGroups.map((group) => (
        <div key={group.artefactType}>
          <GroupHeader group={group} selectedKeys={state.selectedKeys} onToggleGroup={state.toggleGroup} t={t} />
          {group.items.map((item) => (
            <ItemRow
              key={item.itemKey}
              item={item}
              selected={state.selectedKeys.has(item.itemKey)}
              reingest={state.reingestKeys.has(item.itemKey)}
              onToggle={state.toggleItem}
              t={t}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

function ReadyBody({ state, caseId }: { state: ContainerTriageState; caseId: string }): ReactElement | null {
  const { t } = useTranslation('cases')
  if (!state.inventory) return null
  if (state.inventoryOperationId) {
    return (
      <OperationProgress
        caseId={caseId}
        operationId={state.inventoryOperationId}
        onComplete={state.handleInventoryOperationComplete}
      />
    )
  }
  if (state.inventory.inventoryStatus !== 'completed') {
    return (
      <NeedsInventoryState
        status={state.inventory.inventoryStatus}
        onRunInventory={() => { void state.triggerInventory() }}
        t={t}
      />
    )
  }
  return (
    <>
      <KindFilterSelect value={state.kindFilter} onChange={state.setKindFilter} t={t} />
      <GroupsList state={state} />
    </>
  )
}

function SubmittedBody({
  state,
  caseId,
  onClose,
}: {
  state: ContainerTriageState
  caseId: string
  onClose: () => void
}): ReactElement | null {
  const { t } = useTranslation('cases')
  if (!state.submittedRun) return null
  if (state.submittedOperationId) {
    return (
      <OperationProgress
        caseId={caseId}
        operationId={state.submittedOperationId}
        warningCopy={t('containerTriage.runInProgressWarning')}
        onComplete={state.handleRunOperationComplete}
      />
    )
  }
  return <SubmittedState run={state.submittedRun} onClose={onClose} t={t} />
}

function PanelBody({
  state,
  caseId,
  onClose,
}: {
  state: ContainerTriageState
  caseId: string
  onClose: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  if (state.phase === 'loading') return <PanelLoadingState t={t} />
  if (state.phase === 'error' && state.inventory === null) {
    return <LoadErrorState message={state.error ?? ''} onRetry={state.retry} t={t} />
  }
  if (state.phase === 'submitted' && state.submittedRun) {
    return <SubmittedBody state={state} caseId={caseId} onClose={onClose} />
  }
  return (
    <div className="space-y-3">
      {state.error !== null && <SubmitErrorBanner message={state.error} t={t} />}
      <ReadyBody state={state} caseId={caseId} />
    </div>
  )
}

function hasFooter(state: ContainerTriageState): boolean {
  return state.phase !== 'loading' && state.phase !== 'submitted'
}

/**
 * @behaviour Overlay modal (upload-receipt-modal chrome) driving the
 *   loading/ready/confirmingReingest/submitting/submitted/error phases.
 * @errors Load and submit failures both render a retryable banner via
 *   `state.error`/`state.retry`.
 */
export function ContainerTriagePanel({ caseId, receiptId, onClose }: ContainerTriagePanelProps): ReactElement {
  const { t } = useTranslation('cases')
  const state = useContainerTriage(caseId, receiptId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-slate-800">
        <PanelHeader onClose={onClose} t={t} />
        <div className="space-y-4 p-4">
          <PanelBody state={state} caseId={caseId} onClose={onClose} />
          {hasFooter(state) && (
            <SelectionFooter
              selectedCount={state.selectedKeys.size}
              submitting={state.phase === 'submitting'}
              onSubmit={() => { void state.submit() }}
              onClose={onClose}
              t={t}
            />
          )}
        </div>
        {state.pendingReingestItem && (
          <ConfirmDialog
            open
            title={t('containerTriage.reingestConfirmTitle')}
            body={t('containerTriage.reingestConfirmBody', {
              date: state.pendingReingestItem.lastIngestedAt
                ? new Date(state.pendingReingestItem.lastIngestedAt).toLocaleDateString()
                : '',
              count: state.pendingReingestItem.lastRowCount ?? 0,
            })}
            confirmLabel={t('containerTriage.reingestConfirmConfirm')}
            cancelLabel={t('containerTriage.reingestConfirmCancel')}
            onConfirm={state.confirmReingest}
            onCancel={state.cancelReingest}
            destructive
          />
        )}
      </div>
    </div>
  )
}
